import { useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/auth.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useEventBusStore } from '../stores/event-bus.store';
import { useNotificationsStore } from '../stores/notifications.store';
import { useQueryClient } from '@tanstack/react-query';
import { RealtimeEvent } from '@prometeus/contracts';
import api from '../lib/api';
import { NotificationUnreadCountDto } from '@prometeus/contracts';
import { useAiTriggerExecutionStore } from '../stores/ai-trigger-execution.store';
import { taskActivitiesQueryKey } from '../components/taskActivitiesQuery';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000';

// Track processed events to avoid duplicates
const processedEvents = new Set<string>();
const EVENT_DEDUP_WINDOW = 5000; // 5 seconds

// Singleton socket instance
let globalSocket: Socket | null = null;
let globalConnectionParams: { accessToken: string | null; workspaceId: string | null } = {
  accessToken: null,
  workspaceId: null,
};
let listenersInitialized = false; // Track if listeners have been set up

function getEventKey(event: RealtimeEvent): string {
  // Create unique key from event type, timestamp, and relevant data
  const eventType = event.type as string;
  if (eventType === 'task.comment.created' && event.data) {
    const data = event.data as { commentId?: string; taskId?: string };
    return `${eventType}:${data.commentId || event.timestamp}`;
  }
  // For task events, include taskId in the key to avoid false duplicates
  if (event.type.startsWith('task.') && event.data && typeof event.data === 'object') {
    const data = event.data as { id?: string };
    if (data.id) {
      return `${event.type}:${data.id}:${event.timestamp}`;
    }
  }
  if (
    event.type.startsWith('ai.trigger.processing.') &&
    event.data &&
    typeof event.data === 'object'
  ) {
    const data = event.data as { executionId?: string };
    if (data.executionId) {
      return `${event.type}:${data.executionId}`;
    }
  }
  return `${event.type}:${event.timestamp}`;
}

function isEventProcessed(event: RealtimeEvent): boolean {
  const key = getEventKey(event);
  if (processedEvents.has(key)) {
    return true;
  }
  processedEvents.add(key);
  // Clean up old events after window
  setTimeout(() => {
    processedEvents.delete(key);
  }, EVENT_DEDUP_WINDOW);
  return false;
}

export function useWebSocket() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const currentWorkspace = useWorkspaceStore((state) => state.currentWorkspace);
  const workspaceId = currentWorkspace?.id ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    // Skip if parameters haven't changed and socket is already connected
    if (
      globalConnectionParams.accessToken === accessToken &&
      globalConnectionParams.workspaceId === workspaceId &&
      globalSocket?.connected
    ) {
      return;
    }

    if (!accessToken || !workspaceId) {
      // Cleanup previous connection if workspace is no longer available
      if (globalSocket?.connected) {
        console.log('Disconnecting WebSocket: no accessToken or workspace');
        globalSocket.disconnect();
        globalSocket = null;
      }
      globalConnectionParams = { accessToken, workspaceId };
      return;
    }

    // Update params only when we're about to connect
    globalConnectionParams = { accessToken, workspaceId };

    // Disconnect previous socket if exists and params changed
    if (globalSocket?.connected && (
      globalConnectionParams.accessToken !== accessToken ||
      globalConnectionParams.workspaceId !== workspaceId
    )) {
      console.log('Disconnecting previous WebSocket connection due to params change');
      globalSocket.removeAllListeners(); // Remove all listeners before disconnecting
      globalSocket.disconnect();
      globalSocket = null;
      listenersInitialized = false; // Reset flag when disconnecting
    }

    // Only create new connection if we don't have one
    if (!globalSocket) {
      console.log('Setting up WebSocket connection for workspace:', workspaceId);

      // Connect to WebSocket
      globalSocket = io(`${WS_URL}/realtime`, {
        auth: {
          token: accessToken,
        },
        query: {
          token: accessToken,
          workspaceId: workspaceId,
        },
        forceNew: true,
      });

      listenersInitialized = false; // Reset flag when creating new socket
    }

    // Only set up listeners once per socket instance
    if (!listenersInitialized && globalSocket) {
      console.log('Setting up WebSocket event listeners');
      listenersInitialized = true;

      globalSocket.on('connect', () => {
        console.log('WebSocket connected');
        // Note: Backend automatically subscribes on connection if workspaceId is in query
        // Only emit subscribe if workspaceId wasn't in query (shouldn't happen, but just in case)
        // The backend's handleConnection already calls subscribeToWorkspace if workspaceId exists
      });

      globalSocket.on('subscribed', (data: { workspaceId: string }) => {
        console.log('Subscribed to workspace:', data?.workspaceId || workspaceId);
      });

      globalSocket.on('event', (event: RealtimeEvent) => {
        console.log('Realtime event:', event);
        
        // For task.updated events, use a more specific deduplication key
        // that includes taskId to avoid false duplicates
        let shouldSkip = false;
        if (event.type === 'task.updated' && event.data && typeof event.data === 'object') {
          const data = event.data as { id?: string };
          if (data.id) {
            // Create a key that includes taskId and timestamp rounded to seconds
            const timestampSeconds = Math.floor(new Date(event.timestamp).getTime() / 1000);
            const specificKey = `task.updated:${data.id}:${timestampSeconds}`;
            
            if (processedEvents.has(specificKey)) {
              console.log('Skipping duplicate task.updated event:', specificKey);
              shouldSkip = true;
            } else {
              processedEvents.add(specificKey);
              setTimeout(() => processedEvents.delete(specificKey), 2000); // 2 second window
            }
          }
        } else {
          // For other events, use the standard deduplication
          if (isEventProcessed(event)) {
            console.log('Skipping duplicate event:', getEventKey(event));
            shouldSkip = true;
          }
        }
        
        if (shouldSkip) {
          return;
        }
        
        // Handle toast events
        const handleRealtimeEvent = useEventBusStore.getState().handleRealtimeEvent;
        handleRealtimeEvent(event);

        // Get current workspace at the time of event handling
        const currentWs = useWorkspaceStore.getState().currentWorkspace;
        if (!currentWs) return;

        // Invalidate relevant queries based on event type
        if (event.type.startsWith('client.')) {
          queryClient.invalidateQueries({ queryKey: ['clients', currentWs.id] });
          // Invalidate activities when client is created
          if (event.type === 'client.created') {
            queryClient.invalidateQueries({ queryKey: ['activities', 'recent', currentWs.id] });
          }
        } else if (event.type.startsWith('process.')) {
          queryClient.invalidateQueries({ queryKey: ['processes'] });
          // Invalidate activities when process is created
          if (event.type === 'process.created') {
            queryClient.invalidateQueries({ queryKey: ['activities', 'recent', currentWs.id] });
          }
        } else if (event.type.startsWith('member.') || event.type.startsWith('role.')) {
          queryClient.invalidateQueries({ queryKey: ['members', currentWs.id] });
          queryClient.invalidateQueries({ queryKey: ['roles', currentWs.id] });
        } else if (event.type.startsWith('task.')) {
          // Invalidate all task-related queries
          queryClient.invalidateQueries({ queryKey: ['tasks', currentWs.id] });
          queryClient.invalidateQueries({ queryKey: ['user-tasks', currentWs.id] });
          queryClient.invalidateQueries({ queryKey: ['tasks', 'process'] });
          // Invalidate recent activities when task changes
          queryClient.invalidateQueries({ queryKey: ['activities', 'recent', currentWs.id] });
          // If event has processId, invalidate tasks for that process
          if ('processId' in event.data && typeof event.data === 'object' && event.data !== null) {
            const data = event.data as { processId?: string };
            if (data.processId) {
              queryClient.invalidateQueries({ queryKey: ['tasks', currentWs.id, data.processId] });
            }
          }
          
          // Handle task comment events
          const eventType = event.type as string;
          if (eventType === 'task.comment.created') {
            const data = event.data as unknown as { 
              taskId: string; 
              commentId: string; 
              authorMemberId: string;
              authorUserId: string;
              authorName: string;
            };
            
            // Invalidate comments query
            queryClient.invalidateQueries({ queryKey: ['task-comments', data.taskId] });
            
            // Invalidate notifications (may have been mentioned)
            queryClient.invalidateQueries({ queryKey: ['notifications', currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', currentWs.id] });
            
            // Update store count
            (async () => {
              try {
                const response = await api.get<{ data: NotificationUnreadCountDto }>('/notifications/unread-count');
                useNotificationsStore.getState().setUnreadCount(response.data.data.count);
              } catch (error) {
                console.error('Error fetching unread count:', error);
              }
            })();
            
            // Show notification if comment is from another user
            const currentUser = useAuthStore.getState().user;
            if (currentUser && data.authorUserId !== currentUser.id) {
              const addToast = useEventBusStore.getState().addToast;
              addToast(`${data.authorName} comentou em uma task`, 'info');
            }
          } else if (eventType === 'task.comment.deleted') {
            const data = event.data as unknown as { taskId: string; commentId: string };
            queryClient.invalidateQueries({ queryKey: ['task-comments', data.taskId] });
          }
          
          // Handle notification created
          if (event.type === 'notification.created') {
            // Invalidate notifications queries - backend will filter by current user
            queryClient.invalidateQueries({ queryKey: ['notifications', currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', currentWs.id] });
            
            // Update store count
            (async () => {
              try {
                const response = await api.get<{ data: NotificationUnreadCountDto }>('/notifications/unread-count');
                useNotificationsStore.getState().setUnreadCount(response.data.data.count);
              } catch (error) {
                console.error('Error fetching unread count:', error);
              }
            })();
          }
          
          // Handle task assignee changed
          if (event.type === 'task.assignee_changed') {
            const data = event.data as { 
              id: string; 
              processId?: string; 
              workspaceId: string;
              newAssigneeMemberId: string | null;
            };
            
            // Invalidate task queries
            queryClient.invalidateQueries({ queryKey: ['tasks', data.workspaceId || currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['user-tasks', data.workspaceId || currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'process'] });
            
            // Invalidate individual task query to update TaskDetailModal
            if (data.id) {
              queryClient.invalidateQueries({ queryKey: ['tasks', data.id] });
              queryClient.invalidateQueries({ queryKey: taskActivitiesQueryKey(data.id) });
            }
            
            if (data.processId) {
              queryClient.invalidateQueries({ queryKey: ['tasks', data.workspaceId || currentWs.id, data.processId] });
            }
            
            // Invalidate notifications if assignee changed
            queryClient.invalidateQueries({ queryKey: ['notifications', currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', currentWs.id] });
            
            // Update store count
            (async () => {
              try {
                const response = await api.get<{ data: NotificationUnreadCountDto }>('/notifications/unread-count');
                useNotificationsStore.getState().setUnreadCount(response.data.data.count);
              } catch (error) {
                console.error('Error fetching unread count:', error);
              }
            })();
          }
          
          // Handle task updated (may notify assignee)
          if (event.type === 'task.updated') {
            const data = event.data as { id: string; processId?: string; workspaceId: string; changedFields?: string[] };
            console.log('[useWebSocket] Processing task.updated event for task:', data.id, 'changedFields:', data.changedFields);
            
            // Invalidate task queries
            queryClient.invalidateQueries({ queryKey: ['tasks', data.workspaceId || currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['user-tasks', data.workspaceId || currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'process'] });
            
            // Invalidate individual task query to update TaskDetailModal - this is critical!
            if (data.id) {
              console.log('[useWebSocket] Invalidating individual task query:', ['tasks', data.id]);
              
              // Check if query exists and is active
              const queryState = queryClient.getQueryState(['tasks', data.id]);
              console.log('[useWebSocket] Query state:', queryState ? 'exists' : 'not found');
              console.log('[useWebSocket] Query is active:', queryState?.status);
              
              // Force refetch immediately - this will trigger even if query is not "active"
              queryClient.refetchQueries({ 
                queryKey: ['tasks', data.id],
                type: 'active', // Only refetch active queries
              }).then(() => {
                console.log('[useWebSocket] Refetch completed for:', ['tasks', data.id]);
              }).catch((error) => {
                console.error('[useWebSocket] Refetch error:', error);
              });
              
              // Also invalidate to mark as stale
              queryClient.invalidateQueries({ 
                queryKey: ['tasks', data.id],
                refetchType: 'active',
              });
              queryClient.invalidateQueries({ queryKey: taskActivitiesQueryKey(data.id) });
            }
            
            if (data.processId) {
              queryClient.invalidateQueries({ queryKey: ['tasks', data.workspaceId || currentWs.id, data.processId] });
            }
            
            // Invalidate notifications (assignee may have been notified)
            queryClient.invalidateQueries({ queryKey: ['notifications', currentWs.id] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', currentWs.id] });
            
            // Update store count
            (async () => {
              try {
                const response = await api.get<{ data: NotificationUnreadCountDto }>('/notifications/unread-count');
                useNotificationsStore.getState().setUnreadCount(response.data.data.count);
              } catch (error) {
                console.error('Error fetching unread count:', error);
              }
            })();
          }
        } else if (event.type.startsWith('tag.')) {
          // Invalidate tags query when tags are created, updated, or deleted
          queryClient.invalidateQueries({ queryKey: ['tags', currentWs.id] });
        } else if (event.type.startsWith('ai.trigger.processing.')) {
          if (event.workspaceId !== currentWs.id) {
            return;
          }
          const d = event.data as {
            executionId: string;
            triggerKey: string;
            resourceType: string;
            resourceId: string;
            requestedByUserId?: string;
            content?: string;
            errorMessage?: string;
          };
          const aiStore = useAiTriggerExecutionStore.getState();
          if (event.type === 'ai.trigger.processing.started') {
            aiStore.setRunning(currentWs.id, {
              executionId: d.executionId,
              status: 'running',
              triggerKey: d.triggerKey,
              resourceType: d.resourceType,
              resourceId: d.resourceId,
              requestedByUserId: d.requestedByUserId,
            });
          } else if (event.type === 'ai.trigger.processing.finished') {
            if (d.triggerKey === 'tratamento_publicacoes_ia' && d.content != null) {
              aiStore.setPublicationAiContent(d.resourceId, d.content);
            }
            if (
              d.triggerKey === 'busca_movimentos_ia' &&
              d.resourceType === 'cnj_search_item' &&
              typeof d.content === 'string'
            ) {
              queryClient.setQueryData(
                ['cnj-search-movements-ai-summary', currentWs.id, d.resourceId],
                {
                  content: d.content,
                  updatedAt: new Date().toISOString(),
                },
              );
            }
            aiStore.clear(
              currentWs.id,
              d.triggerKey,
              d.resourceType,
              d.resourceId,
            );
            queryClient.invalidateQueries({ queryKey: ['processes'] });
            queryClient.invalidateQueries({
              queryKey: ['publications', currentWs.id],
            });
            if (d.resourceType === 'cnj_search_item') {
              queryClient.invalidateQueries({
                queryKey: [
                  'cnj-search-movements-ai-summary',
                  currentWs.id,
                  d.resourceId,
                ],
              });
            }
          } else if (event.type === 'ai.trigger.processing.failed') {
            aiStore.clear(
              currentWs.id,
              d.triggerKey,
              d.resourceType,
              d.resourceId,
            );
            useEventBusStore
              .getState()
              .addToast(
                d.errorMessage || 'Falha no processamento de IA',
                'error',
              );
            queryClient.invalidateQueries({ queryKey: ['processes'] });
            queryClient.invalidateQueries({
              queryKey: ['publications', currentWs.id],
            });
            if (d.resourceType === 'cnj_search_item') {
              queryClient.invalidateQueries({
                queryKey: [
                  'cnj-search-movements-ai-summary',
                  currentWs.id,
                  d.resourceId,
                ],
              });
            }
          }
        } else if ((event.type as string).startsWith('community.')) {
          // Handle community events (no workspace required)
          // Cast to string since community events are not in the RealtimeEvent union type
          const eventType = event.type as string;
          if (eventType === 'community.article.created') {
            const data = event.data as unknown as { id: string; authorId: string };
            queryClient.invalidateQueries({ queryKey: ['community-articles'] });
            queryClient.invalidateQueries({ queryKey: ['community-article', data.id] });
          } else if (eventType === 'community.article.comment') {
            const data = event.data as unknown as { id: string; articleId: string };
            queryClient.invalidateQueries({ queryKey: ['community-article', data.articleId] });
          } else if (eventType === 'community.article.reaction') {
            const data = event.data as unknown as { articleId: string };
            queryClient.invalidateQueries({ queryKey: ['community-article', data.articleId] });
            queryClient.invalidateQueries({ queryKey: ['community-articles'] });
          } else if (eventType === 'community.connection.request' || eventType === 'community.connection.accepted') {
            queryClient.invalidateQueries({ queryKey: ['community-connections'] });
            queryClient.invalidateQueries({ queryKey: ['connection-status'] });
          } else if (eventType === 'community.job.created') {
            queryClient.invalidateQueries({ queryKey: ['community-jobs'] });
          } else if (eventType === 'community.job.application') {
            const data = event.data as unknown as { jobId: string };
            queryClient.invalidateQueries({ queryKey: ['community-job', data.jobId] });
            queryClient.invalidateQueries({ queryKey: ['community-jobs'] });
          }
        } else {
          const eventType = event.type as string;
          if (eventType === 'process.archived') {
            // Invalidate processes queries when a process is archived
            const data = event.data as { workspaceId: string; clientId?: string };
            queryClient.invalidateQueries({ queryKey: ['processes', data.workspaceId] });
            if (data.clientId) {
              queryClient.invalidateQueries({ queryKey: ['processes', data.workspaceId, data.clientId] });
            }
          } else if (eventType === 'publication.treated') {
            // Invalidate publications queries when a publication is treated
            const data = event.data as { workspaceId: string };
            queryClient.invalidateQueries({ queryKey: ['publications', data.workspaceId] });
            queryClient.invalidateQueries({ queryKey: ['publications-count', data.workspaceId] });
          } else if (eventType.startsWith('chat.')) {
            // Invalidate chat-related queries
            if (eventType.startsWith('chat.channel.')) {
              queryClient.invalidateQueries({ queryKey: ['chats', 'channels', currentWs.id] });
              // Invalidate channel details if specific channel was affected
              if (event.data && typeof event.data === 'object' && 'id' in event.data) {
                const data = event.data as { id?: string };
                if (data.id) {
                  queryClient.invalidateQueries({ queryKey: ['chats', 'channel', data.id] });
                  queryClient.invalidateQueries({ queryKey: ['chats', 'channel-members', data.id] });
                }
              }
            }
            if (eventType === 'chat.channel.member.added' || eventType === 'chat.channel.member.removed') {
              const data = event.data as { channelId?: string };
              if (data.channelId) {
                queryClient.invalidateQueries({ queryKey: ['chats', 'channel-members', data.channelId] });
                queryClient.invalidateQueries({ queryKey: ['chats', 'channels', currentWs.id] });
              }
            }
            if (eventType.startsWith('chat.message.')) {
              const data = event.data as { channelId?: string };
              if (data.channelId) {
                queryClient.invalidateQueries({ queryKey: ['chats', 'messages', data.channelId] });
              }
              // Also invalidate channels list to update unread counts
              queryClient.invalidateQueries({ queryKey: ['chats', 'channels', currentWs.id] });
            }
          } else if (eventType.startsWith('gamification.')) {
            // Invalidate gamification-related queries
            if (eventType === 'gamification.points_awarded' || eventType === 'gamification.level_up') {
              const data = event.data as { membershipId?: string };
              // Invalidate stats for the affected member
              queryClient.invalidateQueries({ queryKey: ['gamification', 'stats', currentWs.id] });
            }
            if (eventType === 'gamification.benefit_redeemed' || eventType === 'gamification.redemption_status_changed') {
              // Invalidate benefits and redemptions
              queryClient.invalidateQueries({ queryKey: ['benefits', currentWs.id] });
              queryClient.invalidateQueries({ queryKey: ['benefits', 'redemptions', currentWs.id] });
              // Also invalidate stats to update points
              queryClient.invalidateQueries({ queryKey: ['gamification', 'stats', currentWs.id] });
            }
          }
        }
      });

      globalSocket.on('error', (error) => {
        console.error('WebSocket error:', error);
      });

      globalSocket.on('disconnect', () => {
        console.log('WebSocket disconnected');
        listenersInitialized = false; // Reset flag on disconnect
      });
    }

    return () => {
      // Don't disconnect on unmount - keep connection alive for other components
      // Only disconnect if params changed (handled above)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, workspaceId]);

  return globalSocket;
}

